import { useState } from "react";
import "./modal.css";
import iwmiLogo from "../../assets/logo/iwmi_blue.jpg";
import howto01 from "../../assets/others-image/howto-01.png";
import howto02 from "../../assets/others-image/howto-02.png";
import howto03 from "../../assets/others-image/howto-03.png";
import howto04 from "../../assets/others-image/howto-04.png";
import howto05 from "../../assets/others-image/howto-05.png";
import howto06 from "../../assets/others-image/howto-06.png";
import howto07 from "../../assets/others-image/howto-07.png";

const METHODOLOGY_TITLE = "Methodology";
const METHODOLOGY_TEXT = [
  "WasteWise is a decision support tool (DST) that helps users select sustainable circular economy (CE) business models for resource recovery and reuse (RRR) by combining technical, social, economic, and environmental considerations through a structured, multi-criteria decision analysis (MCDA) approach.",
  "At its core, the DST follows a four-stage methodology:",
  "<strong>User-Defined Inputs:</strong> Users begin by specifying key contextual information, including the waste stream type (e.g., food waste, agro-industrial waste), business objective (e.g., cost recovery, profit maximization), and the preferred type of value-added product (e.g., energy, water, or nutrient recovery). Users also identify their location, allowing the tool to pull region-specific data on waste generation, availability, and regulatory context.",
  "<strong>Criteria and Indicator Selection:</strong> The tool evaluates business model options using 5 criteria and 18 indicators spanning technical, financial, social, environmental, and institutional domains. These criteria are informed by expert judgment, literature, and stakeholder input. Indicators include both quantitative metrics (e.g., energy recovery potential, job creation, emissions reduction) and qualitative assessments (e.g., institutional readiness, social acceptability). Users can tailor the relative importance of each criterion through an intuitive interface.",
  "<strong>Weighting and Scoring via AHP:</strong> The DST uses the Analytical Hierarchy Process (AHP), a widely adopted MCDA method, to assign weights to each criterion based on user preferences. Users conduct pairwise comparisons of criteria to express their priorities. AHP then translates these qualitative judgments into numerical weights. The tool combines these weights with performance data to compute a composite score for each CE business model.",
  "<strong>Geospatial Integration and Visualization:</strong> A built-in GIS component supports site-specific analysis. It maps waste generation hotspots, identifies potential RRR facility sites, and analyzes site suitability using spatial data layers (e.g., transportation networks, environmental zones). This helps users visualize where and how specific CE models can be implemented most effectively.",
  "WasteWise ultimately generates a decision matrix that compares business model alternatives across the selected criteria. It highlights trade-offs, ranks options, and presents recommendations tailored to the user’s goals and location. For the top-ranked model, users can generate a Business Model Canvas (BMC), detailing operations, customer segments, value propositions, and environmental/social benefits.",
  "WasteWise is ideal for small enterprises, planners, and policymakers seeking evidence-based guidance for CE initiatives. By enabling scenario testing, stakeholder engagement, and data-driven planning, it supports the transition to nature-positive and economically viable waste reuse solutions.",
];

const PRIVACY_TITLE = "Privacy and Liability";
export const PRIVACY_VERSION = "2026-10-01";
const PRIVACY_TEXT = [
  "1. WasteWise is for informational and planning purposes only; IWMI makes no warranties regarding the accuracy or suitability of its outputs, and use is at your own risk. IWMI is not liable for any damages, including but not limited to financial losses or regulatory issues, arising from reliance on the Decision Support Tool (DST). You are responsible for verifying recommendations and ensuring compliance with local laws.",
  "2. WasteWise processes your data on the basis of your explicit consent for report-download analytics and otherwise on consent/legitimate interests under applicable data protection laws, using industry standard encryption and access controls to ensure security.",
  "3. Your data safety is important to us. All data you input into the application is visible only to you through your personal dashboard, except report-download contact details (plant name, email, occupation, stated use, download date and consent record), which are visible to Admin-role IWMI staff as described below. This ensures that your project details, preferences, and results remain private and secure.",
  "4. Selected information may be accessed by authorized IWMI's team members solely for research purposes and for legitimate interests such as tool performance, system maintenance, and user support. In addition, when you tick the consent checkbox in the Download Report popup, we store your email, occupation, stated use, plant name, download date, consent timestamp and privacy version (v2026-10-01) to track uptake and improve WasteWise. Full emails are shown in the admin Downloaded Reports table to Admin-role staff only. Administrators will never use your data for marketing or external sharing.",
  "5. If you use the Quick Analysis feature, your data will not be saved in your dashboard. Instead, it will be temporarily stored on IWMI's secure servers for up to one year, after which it will be permanently deleted. This data is not linked to your identity.",
  "6. Report-download records are kept until you request deletion, with an annual review by the IWMI team. Anonymized data—meaning information that has been stripped of all personally identifying details—may be used in aggregate for research purposes. This supports our mission to improve the understanding and diffusion of circular economy business models, while helping to enhance the functionality of this tool.",
  "7. You may request access to, or deletion of, your stored data (including a report-download record) at any time by contacting IWMI's team at iwmi-wastewise@cgiar.org with the subject line 'WasteWise'. Admins can also delete a record directly from the Downloaded Reports table.",
  "8. By ticking the consent checkbox and by using the DST, you agree to the terms as stated above.",
];

const HOW_TO_USE_TITLE = "How to use the tool";
const HOW_TO_USE_SUBTITLE =
  "Follow the step-by-step slideshow below to get a full overview of the process.";

const SLIDESHOW_DATA = [
  {
    id: 1,
    image: howto01,
    title: "Framing the Challenge",
    description:
      "Building a circular bioeconomy starts with the right data. This tool helps you turn waste into opportunity with clear, useful insights to support smarter decisions.",
  },
  {
    id: 2,
    image: howto02,
    title: "Set Your Region and Goals",
    description:
      "Select your region and what you want to achieve. Your choices guide the tool to show the most relevant options and results for your needs.",
  },
  {
    id: 3,
    image: howto03,
    title: "Choose Waste and Outputs",
    description:
      "Tell the tool what kind of waste you have access to and what you’d like to turn it into. You can choose energy, nutrient, clean water or proteins.",
  },
  {
    id: 4,
    image: howto04,
    title: "Map Waste Sources",
    description:
      "Use the interactive map to spot where waste is available. It’s a quick way to find high-potential areas to source your materials from.",
  },
  {
    id: 5,
    image: howto05,
    title: "Select Facility Location and Set It Up",
    description:
      "Using the many GIS map overlays to help you pinpoint an ideal location for your plant. Set a few details, and launch the analysis. The tool does the heavy lifting from there.",
  },
  {
    id: 6,
    image: howto06,
    title: "Review Results Visually",
    description:
      "Check out your results in a clean, visual dashboard. Compare business models, dig into the numbers, and see what works best.",
  },
  {
    id: 7,
    image: howto07,
    title: "Save and Compare Analyses",
    description:
      "Create your own profile to save results. You’ll be able to compare different scenarios and keep track of all your ideas in one place.",
  },
];

const CREDITS_TITLE = "Credits";
const CREDITS_INTRO = (
  <>
    <strong>WasteWise</strong> was developed by the{" "}
    <a href="https://www.iwmi.org/" target="_blank" rel="noopener noreferrer">
      International Water Management Institute (IWMI)
    </a>{" "}
    as part of its commitment to supporting circular economy transitions through data-driven decision-making.
  </>
);
const CREDITS_ACKNOWLEDGMENT =
  "We gratefully acknowledge the contributions of the following team members:";
const CREDITS_FUNDING =
  "This work was made possible with the generous support of our funding partners. We thank all contributors to the CGIAR Science Program on Multifunctional Landscape and the CGIAR Trust Fund for enabling this research.";

const CREDITS_IWMI_TEAM_TITLE = "IWMI TEAM";
const CREDITS_IWMI_TEAM_MEMBERS = [
  "Tosin Somorin – Project Coordination/Technical & Environmental Science",
  "Solomie Gebrezgabher – Project Leader & Economics",
  "Surajit Ghosh – Spatial Data Science",
  "Avinandan Taron – Socioeconomics",
  "Shweta Yadav – GIS/Data Science",
  "Eric G. Nartey – Technical & Environmental Science",
  "Desmond Ayertey – IWMI Consultant",
  "Andoh Amponsah – IWMI Consultant",
];

const CREDITS_CREATIVE_TEAM_TITLE = "DEVELOPMENT TEAM";
const CREDITS_BETA_COMPANY = "Beta Development – Umwelt Design";
const CREDITS_BETA_MEMBERS = [
  "Alberto Miti – Project Management",
  "Pier Giorgio Provenzano – Tech Lead & UX",
  "Chhavi Jatwani – Research & UI",
  "Arialdo Martini – Lead Software Development",
];
const CREDITS_ALPHA_COMPANY = "Alpha Development – Sustaintel";
const CREDITS_ALPHA_MEMBERS = [
  "Debanjan Bandyopadhyay – Project Management",
  "Meghma Mitra – GIS/Data Science",
  "Arka Roy - Software Development",
];
const CREDITS_PIXELRUN_COMPANY = "Current Release – Pixelrun UK";
const CREDITS_PIXELRUN_MEMBERS = [
  "Pier Giorgio Provenzano – Technical Director",
  "Arialdo Martini – Lead Software Development",
];

const parseTextWithHTML = (text: string) => {
  const parts = text.split(/(<strong>.*?<\/strong>)/g);

  return parts.map((part, index) => {
    if (part.startsWith("<strong>") && part.endsWith("</strong>")) {
      const content = part.replace(/<\/?strong>/g, "");
      return <strong key={index}>{content}</strong>;
    }
    return part;
  });
};

export type ModalType = "methodology" | "howToUse" | "privacy" | "credits";

interface ModalProps {
  type: ModalType;
  isOpen: boolean;
  onClose: () => void;
  isInline?: boolean;
  isOffsetModal?: boolean;
  showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  type,
  isOpen,
  onClose,
  isInline = false,
  isOffsetModal = false,
  showCloseButton = true,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % SLIDESHOW_DATA.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + SLIDESHOW_DATA.length) % SLIDESHOW_DATA.length
    );
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (type) {
      case "methodology":
        return (
          <div className="modal-body">
            <h2 className="modal-title">{METHODOLOGY_TITLE}</h2>
            <div className="modal-text">
              {METHODOLOGY_TEXT.map((paragraph, index: number) => (
                <p key={index}>{parseTextWithHTML(paragraph)}</p>
              ))}
            </div>
          </div>
        );

      case "privacy":
        return (
          <div className="modal-body">
            <h2 className="modal-title">{PRIVACY_TITLE}</h2>
            <div className="modal-text">
              {PRIVACY_TEXT.map((paragraph, index: number) => {
                const email = "iwmi-wastewise@cgiar.org";
                if (!paragraph.includes(email)) return <p key={index}>{paragraph}</p>;
                const parts = paragraph.split(email);
                return (
                  <p key={index}>
                    {parts[0]}
                    <a
                      href={`mailto:${email}?subject=WasteWise`}
                      className="modal-credits-link"
                    >
                      {email}
                    </a>
                    {parts[1]}
                  </p>
                );
              })}
            </div>
          </div>
        );

      case "howToUse":
        return (
          <div className="modal-body">
            <h2 className="modal-title">{HOW_TO_USE_TITLE}</h2>
            <p className="modal-how-to-use-subtitle">{HOW_TO_USE_SUBTITLE}</p>

            <div className="modal-slideshow-container">
              <div className="modal-slideshow-content">
                <div className="modal-slideshow-image-container">
                  <button
                    className="modal-slideshow-nav-btn modal-slideshow-prev"
                    onClick={prevSlide}
                    disabled={currentSlide === 0}
                  >
                    ‹
                  </button>

                  <img
                    src={SLIDESHOW_DATA[currentSlide].image}
                    alt={SLIDESHOW_DATA[currentSlide].title}
                    className="modal-slideshow-image"
                  />

                  <button
                    className="modal-slideshow-nav-btn modal-slideshow-next"
                    onClick={nextSlide}
                    disabled={currentSlide === SLIDESHOW_DATA.length - 1}
                  >
                    ›
                  </button>
                </div>

                <div className="modal-slideshow-indicators">
                  {SLIDESHOW_DATA.map((_, index: number) => (
                    <button
                      key={index}
                      className={`modal-slideshow-indicator ${
                        currentSlide === index ? "active" : ""
                      }`}
                      onClick={() => goToSlide(index)}
                    />
                  ))}
                </div>

                <div className="modal-slideshow-text">
                  <p className="modal-slideshow-description">
                    <strong>{SLIDESHOW_DATA[currentSlide].title}.</strong>{" "}
                    {SLIDESHOW_DATA[currentSlide].description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "credits":
        return (
          <div className="modal-body modal-credits-body">
            <h2 className="modal-title">{CREDITS_TITLE}</h2>
            <div className="modal-text modal-credits-text">
              <p>{CREDITS_INTRO}</p>

              <div className="modal-credits-logo-container">
                <img
                  src={iwmiLogo}
                  alt="IWMI Logo"
                  className="modal-credits-logo"
                />
              </div>

              <p>{CREDITS_ACKNOWLEDGMENT}</p>

              <div className="modal-credits-section">
                <h3 className="modal-credits-section-title">
                  {CREDITS_IWMI_TEAM_TITLE}
                </h3>
                <div className="modal-credits-team-grid">
                  <div className="modal-credits-team-column">
                    {CREDITS_IWMI_TEAM_MEMBERS.slice(0, 4).map(
                      (member, index: number) => (
                        <p key={index} className="modal-credits-member">
                          {member}
                        </p>
                      )
                    )}
                  </div>
                  <div className="modal-credits-team-column">
                    {CREDITS_IWMI_TEAM_MEMBERS.slice(4).map(
                      (member, index: number) => (
                        <p key={index} className="modal-credits-member">
                          {member}
                        </p>
                      )
                    )}
                  </div>
                </div>
              </div>

              <p>{CREDITS_FUNDING}</p>

              <div className="modal-credits-section">
                <h3 className="modal-credits-section-title">
                  {CREDITS_CREATIVE_TEAM_TITLE}
                </h3>
                <div className="modal-credits-creative-grid">
                  <div className="modal-credits-creative-column">
                    <p className="modal-credits-company">
                      {CREDITS_PIXELRUN_COMPANY}
                    </p>
                    {CREDITS_PIXELRUN_MEMBERS.map((member, index: number) => (
                      <p key={index} className="modal-credits-member">
                        {member}
                      </p>
                    ))}
                    <p className="modal-credits-member" style={{ marginTop: "6px" }}>
                      <a
                        href="https://pixelrun.co.uk"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="modal-credits-link"
                      >
                        Pixelrun UK
                      </a>
                      , official developer partner for WasteWise.
                    </p>
                  </div>
                  <div className="modal-credits-creative-column">
                    <p className="modal-credits-company">
                      {CREDITS_BETA_COMPANY}
                    </p>
                    {CREDITS_BETA_MEMBERS.map((member, index: number) => (
                      <p key={index} className="modal-credits-member">
                        {member}
                      </p>
                    ))}
                  </div>
                  <div className="modal-credits-creative-column">
                    <p className="modal-credits-company">
                      {CREDITS_ALPHA_COMPANY}
                    </p>
                    {CREDITS_ALPHA_MEMBERS.map((member, index: number) => (
                      <p key={index} className="modal-credits-member">
                        {member}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              <p className="modal-credits-footer">
                To learn more about this initiative, visit{" "}
                <a
                  href="https://wastewise.iwmi.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modal-credits-link"
                >
                  wastewise.iwmi.org
                </a>
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isInline) {
    return <div className="modal-inline-content">{renderContent()}</div>;
  }

  const overlayClass = isOffsetModal
    ? "modal-overlay modal-overlay-offset"
    : "modal-overlay";

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className="modal-wrapper">
        {showCloseButton && (
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        )}
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Modal;




